import { useState, useRef, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Mail, MapPin, Send } from 'lucide-react';
import { sendContactEmail } from '../api/contact';

export default function Contact() {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    company: '',
    message: '',
  });
  
  const [formStatus, setFormStatus] = useState<'idle' | 'sending' | 'success' | 'error'>('idle');
  const [errorMessage, setErrorMessage] = useState('');
  const turnstileRef = useRef<HTMLDivElement>(null);
  const [widgetId, setWidgetId] = useState<string | null>(null);

  // Initialize Turnstile widget on mount
  useEffect(() => {
    const initTurnstile = () => {
      if ((window as any).turnstile && turnstileRef.current && !widgetId) {
        try {
          // Clear any existing widgets in this container first
          turnstileRef.current.innerHTML = '';
          
          const id = (window as any).turnstile.render(turnstileRef.current, {
            sitekey: '0x4AAAAAAB-aNrPmpnpY_mxi',
            theme: 'dark',
            appearance: 'always',
            callback: () => {
              console.log('Turnstile verified');
            },
          });
          setWidgetId(id);
        } catch (error) {
          console.error('Error rendering Turnstile:', error);
        }
      }
    };

    // Wait for Turnstile script to load
    if ((window as any).turnstile) {
      initTurnstile();
    } else {
      const checkTurnstile = setInterval(() => {
        if ((window as any).turnstile) {
          clearInterval(checkTurnstile);
          initTurnstile();
        }
      }, 100);

      return () => clearInterval(checkTurnstile);
    }

    // Cleanup on unmount
    return () => {
      if (widgetId !== null && (window as any).turnstile) {
        try {
          (window as any).turnstile.remove(widgetId);
        } catch (error) {
          console.error('Error removing Turnstile:', error);
        }
      }
    };
  }, [widgetId]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setFormStatus('sending');
    setErrorMessage('');
    
    // Wait a moment for Turnstile to be ready (if it just loaded)
    await new Promise(resolve => setTimeout(resolve, 100));
    
    // Get Turnstile token
    const turnstileToken = (window as any).turnstile?.getResponse();
    
    if (!turnstileToken) {
      setFormStatus('error');
      setErrorMessage('Security verification failed. Please wait a moment and try again, or email us directly at Cameron@cloudlaunch.au');
      // Try to explicitly render Turnstile widget if it's not showing
      try {
        (window as any).turnstile?.render?.('#turnstile-widget');
      } catch (e) {
        console.error('Turnstile render error:', e);
      }
      return;
    }
    
    try {
      const result = await sendContactEmail({
        ...formData,
        turnstileToken,
      });
      
      if (result.success) {
        setFormStatus('success');
        setFormData({
          name: '',
          email: '',
          company: '',
          message: '',
        });
        // Reset Turnstile widget
        if (widgetId !== null && (window as any).turnstile) {
          (window as any).turnstile.reset(widgetId);
        }
        // Reset success message after 5 seconds
        setTimeout(() => setFormStatus('idle'), 5000);
      } else {
        setFormStatus('error');
        setErrorMessage('Failed to send message. Please try emailing us directly at Cameron@cloudlaunch.au');
      }
    } catch (error) {
      setFormStatus('error');
      setErrorMessage('An error occurred. Please try again or email us directly at Cameron@cloudlaunch.au');
      console.error('Contact form error:', error);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    setFormData(prev => ({
      ...prev,
      [e.target.name]: e.target.value,
    }));
  };

  return (
    <div className="min-h-screen pt-16">
      {/* Hero */}
      <section className="py-24 bg-gradient-to-br from-night-900 via-night-800 to-night-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <h1 className="text-5xl md:text-6xl font-bold mb-6">
              Get in <span className="bg-gradient-to-r from-launch-blue via-launch-purple to-launch-orange bg-clip-text text-transparent">Touch</span>
            </h1>
            <p className="text-xl text-offwhite/80 max-w-3xl mx-auto">
              Let's discuss your project and how we can help bring it to life
            </p>
          </motion.div>
        </div>
      </section>

      {/* Contact Section */}
      <section className="py-24 bg-night-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            {/* Contact Info */}
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
            >
              <h2 className="text-3xl font-bold mb-6">Contact Information</h2>
              <p className="text-offwhite/70 mb-8">
                Fill out the form and we'll get back to you within 24 hours. 
                For urgent inquiries, please call us directly at{' '}
                <a href="tel:+61721127733" className="text-launch-blue hover:text-launch-purple transition-colors">
                  07 2112 7733
                </a>.
              </p>

              <div className="space-y-6">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 rounded-lg bg-launch-blue/10 flex items-center justify-center flex-shrink-0">
                    <Mail className="text-launch-blue" size={24} />
                  </div>
                  <div>
                    <h3 className="font-semibold mb-1">Email</h3>
                    <a href="mailto:hello@cloudlaunch.com" className="text-offwhite/70 hover:text-launch-blue transition-colors">
                      hello@cloudlaunch.com
                    </a>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 rounded-lg bg-launch-purple/10 flex items-center justify-center flex-shrink-0">
                    <svg className="text-launch-purple" width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" fill="none"/>
                    </svg>
                  </div>
                  <div>
                    <h3 className="font-semibold mb-1">Phone</h3>
                    <a href="tel:+61721127733" className="text-offwhite/70 hover:text-launch-purple transition-colors">
                      07 2112 7733
                    </a>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 rounded-lg bg-launch-orange/10 flex items-center justify-center flex-shrink-0">
                    <MapPin className="text-launch-orange" size={24} />
                  </div>
                  <div>
                    <h3 className="font-semibold mb-1">Location</h3>
                    <p className="text-offwhite/70">
                      Australia-based<br />
                      Working with clients globally
                    </p>
                  </div>
                </div>
              </div>
            </motion.div>

            {/* Contact Form */}
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
            >
              <form onSubmit={handleSubmit} className="bg-night-700 rounded-xl p-8">
                <div className="space-y-6">
                  <div>
                    <label htmlFor="name" className="block text-sm font-medium mb-2">
                      Name *
                    </label>
                    <input
                      type="text"
                      id="name"
                      name="name"
                      required
                      value={formData.name}
                      onChange={handleChange}
                      className="w-full px-4 py-3 rounded-lg bg-night-600 text-offwhite border border-night-500 focus:border-launch-blue focus:outline-none"
                    />
                  </div>

                  <div>
                    <label htmlFor="email" className="block text-sm font-medium mb-2">
                      Email *
                    </label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      required
                      value={formData.email}
                      onChange={handleChange}
                      className="w-full px-4 py-3 rounded-lg bg-night-600 text-offwhite border border-night-500 focus:border-launch-blue focus:outline-none"
                    />
                  </div>

                  <div>
                    <label htmlFor="company" className="block text-sm font-medium mb-2">
                      Company
                    </label>
                    <input
                      type="text"
                      id="company"
                      name="company"
                      value={formData.company}
                      onChange={handleChange}
                      className="w-full px-4 py-3 rounded-lg bg-night-600 text-offwhite border border-night-500 focus:border-launch-blue focus:outline-none"
                    />
                  </div>

                  <div>
                    <label htmlFor="message" className="block text-sm font-medium mb-2">
                      Message *
                    </label>
                    <textarea
                      id="message"
                      name="message"
                      required
                      rows={6}
                      value={formData.message}
                      onChange={handleChange}
                      className="w-full px-4 py-3 rounded-lg bg-night-600 text-offwhite border border-night-500 focus:border-launch-blue focus:outline-none resize-none"
                    />
                  </div>

                  {/* Cloudflare Turnstile */}
                  <div className="space-y-2">
                    <label className="block text-sm font-medium mb-2">
                      Security Verification *
                    </label>
                    <div 
                      ref={turnstileRef}
                      className="min-h-[65px]"
                    ></div>
                    <p className="text-xs text-offwhite/50">
                      Please complete the verification above to submit the form.
                    </p>
                  </div>

                  <button
                    type="submit"
                    disabled={formStatus === 'sending'}
                    className="w-full px-6 py-3 rounded-lg bg-gradient-to-r from-launch-blue to-launch-purple text-white font-medium hover:opacity-90 transition-opacity disabled:opacity-50 flex items-center justify-center"
                  >
                    {formStatus === 'sending' ? (
                      'Sending...'
                    ) : (
                      <>
                        Send Message
                        <Send className="ml-2" size={20} />
                      </>
                    )}
                  </button>

                  {formStatus === 'success' && (
                    <motion.div 
                      initial={{ opacity: 0, y: -10 }}
                      animate={{ opacity: 1, y: 0 }}
                      className="p-4 rounded-lg bg-green-500/10 border border-green-500/20 text-green-400"
                    >
                      ✓ Thank you! We'll get back to you within 24 hours.
                    </motion.div>
                  )}

                  {formStatus === 'error' && (
                    <motion.div 
                      initial={{ opacity: 0, y: -10 }}
                      animate={{ opacity: 1, y: 0 }}
                      className="p-4 rounded-lg bg-red-500/10 border border-red-500/20 text-red-400"
                    >
                      {errorMessage || 'Something went wrong. Please try again.'}
                    </motion.div>
                  )}
                </div>
              </form>
            </motion.div>
          </div>
        </div>
      </section>
    </div>
  );
}
